import { MAX_REQUEST_BYTES, SOURCE_MAX } from './limits';

/**
 * Column mapping: which file column feeds which import field, the request
 * body built from it, and the checks made before it is sent.
 *
 * The vendor name is a printed name, and the display name too when no
 * display name column is mapped (ruling 2). Without an external id column
 * the vendor number is the id (ruling 1 of 2c); a row with neither is held
 * by the server as missing_id.
 */
export const FIELDS = [
  { key: 'name', label: 'Vendor name', required: true, hint: 'The name printed on invoices. Also the display name when no display name column is chosen.' },
  { key: 'display_name', label: 'Display name', hint: 'Your own name for the vendor.' },
  { key: 'vendor_number', label: 'Vendor number', hint: 'The number your exports carry.' },
  { key: 'external_id', label: 'External id', hint: 'The id your system gives the vendor. Without one, the vendor number is used as the id.' },
  { key: 'printed_names', label: 'Other printed names', hint: 'More names the vendor prints, separated by semicolons.' },
];

/** Header names each field is guessed from, after normalizeHeader. In priority order. */
export const HEADER_GUESSES = {
  name: ['vendorname', 'suppliername', 'payeename', 'payee', 'vendor', 'supplier', 'name', 'companyname', 'company'],
  display_name: ['displayname', 'shortname', 'dba', 'tradename'],
  vendor_number: ['vendornumber', 'vendorno', 'vendornum', 'vendorcode', 'vendorid', 'suppliernumber', 'supplierno', 'suppliercode', 'supplierid', 'accountnumber', 'accountno', 'acctno', 'number', 'no'],
  external_id: ['externalid', 'erpid', 'recordid', 'internalid', 'id'],
  printed_names: ['printednames', 'printedname', 'aliases', 'alias', 'alternatenames', 'othernames', 'aka', 'akas'],
};

/**
 * The CSV delimiters the Columns step offers; '' lets the parser detect one.
 * Kept here, not in parseCsv.js, so the page does not pull papaparse into
 * the main bundle.
 */
export const DELIMITERS = [
  { value: '', label: 'Detect' },
  { value: ',', label: 'Comma' },
  { value: ';', label: 'Semicolon' },
  { value: '\t', label: 'Tab' },
  { value: '|', label: 'Pipe' },
];

export const normalizeHeader = (h) => String(h || '').toLowerCase().replace(/[\s_\-.#:]+/g, '');

/**
 * The headers a field is guessed from: its HEADER_GUESSES, then its own label
 * and key (ruling 44), so a file using the page's own wording maps.
 */
export const fieldGuesses = (f) => [...new Set([...HEADER_GUESSES[f.key], normalizeHeader(f.label), normalizeHeader(f.key)])];

/** { name: columnIndex | null, ... }. Each column is used at most once. */
export function guessMapping(header) {
  const norm = header.map(normalizeHeader);
  const used = new Set();
  const mapping = {};
  for (const f of FIELDS) {
    mapping[f.key] = null;
    for (const guess of fieldGuesses(f)) {
      const col = norm.findIndex((h, i) => h === guess && !used.has(i));
      if (col >= 0) {
        mapping[f.key] = col;
        used.add(col);
        break;
      }
    }
  }
  return mapping;
}

/** The header names of the columns no field uses (Decision 2: said, not imported). */
export function unusedColumns(header, mapping) {
  const used = new Set(Object.values(mapping).filter((c) => c !== null && c !== undefined));
  return header.filter((_, i) => !used.has(i));
}

/** The fields among vendor number and external id whose column held numbers in a workbook. */
export function numericIdFields(parsed, mapping) {
  const numeric = new Set(parsed.numericColumns || []);
  return FIELDS.filter((f) => (f.key === 'vendor_number' || f.key === 'external_id')
    && mapping[f.key] !== null && mapping[f.key] !== undefined && numeric.has(mapping[f.key]));
}

export const splitPrintedNames = (cell) => String(cell || '').split(';').map((s) => s.trim()).filter(Boolean);

/** The rows of the request: only mapped, non-empty cells are sent. */
export function buildImportRows(parsed, mapping) {
  return parsed.rows.map(({ row, cells }) => {
    const out = { row };
    for (const key of ['name', 'display_name', 'vendor_number', 'external_id']) {
      const col = mapping[key];
      if (col === null || col === undefined) continue;
      const value = cells[col] || '';
      if (value !== '') out[key] = value;
    }
    if (mapping.printed_names !== null && mapping.printed_names !== undefined) {
      const names = splitPrintedNames(cells[mapping.printed_names]);
      if (names.length > 0) out.printed_names = names;
    }
    return out;
  });
}

/** The server's rule (ruling 8): trimmed, lower case, 1 to 64 characters, no control characters. */
export function normalizeSource(input) {
  const value = String(input || '').trim().toLowerCase();
  if (value === '') return { error: 'Enter a source label.' };
  if (value.length > SOURCE_MAX) return { error: `The source label can be at most ${SOURCE_MAX} characters.` };
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return { error: 'The source label cannot contain control characters.' };
  return { value };
}

/** The UTF-8 size of a string, without TextEncoder (jsdom 16 has none). */
export function utf8Length(s) {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) { n += 4; i++; } else n += 3;
  }
  return n;
}

export const REQUEST_TOO_LARGE = 'The vendor rows are too large to send in one import. Split the file into smaller files and import each one.';

/** The request body, or the reason it cannot be sent. */
export function buildImportRequest({ parsed, mapping, source, fileName, link }) {
  if (mapping.name === null || mapping.name === undefined) return { error: 'Choose the column that holds the vendor name.' };
  const s = normalizeSource(source);
  if (s.error) return { error: s.error };
  const body = { source: s.value, file_name: fileName ? String(fileName).slice(0, 255) : null, rows: buildImportRows(parsed, mapping) };
  if (link) body.link = link;
  if (utf8Length(JSON.stringify(body)) > MAX_REQUEST_BYTES) return { error: REQUEST_TOO_LARGE };
  return { body };
}
